import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { RELEASES } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

export const ComingSoon = () => {
  const upcoming = RELEASES.filter((r) => r.status === "coming");
  if (upcoming.length === 0) return null;

  return (
    <section
      id="coming-soon"
      data-testid="coming-soon-section"
      className="relative w-full bg-bg-2 py-24 md:py-32 border-t border-[color:var(--line)] overflow-hidden"
    >
      {/* Subtle gold glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 50% 30% at 50% 50%, rgba(201,169,97,0.10), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 08 — Preview
            </p>
            <h2
              data-testid="coming-soon-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              Coming{" "}
              <span className="font-editorial text-gold-hi">soon</span>
            </h2>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="max-w-md text-sm text-muted leading-relaxed"
          >
            The next chapter — new productions releasing on international labels.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger(0.14, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
        >
          {upcoming.map((r, i) => (
            <motion.article
              key={r.title}
              variants={fadeUp}
              data-testid={`coming-soon-card-${i}`}
              className="group relative overflow-hidden rounded-sm border border-[color:var(--gold)]/30 bg-gradient-to-br from-[color:var(--gold)]/[0.06] to-transparent p-10 md:p-14 transition-all duration-700 hover:border-[color:var(--gold)]/60"
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/40 bg-[#0a0a0a]/50 px-3 py-1 backdrop-blur">
                  <Sparkles size={12} className="text-gold" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-ink/90">
                    Coming Soon
                  </span>
                </span>
                <span className="font-editorial text-2xl text-[color:var(--gold-hi)]/70">
                  0{i + 1}
                </span>
              </div>

              <div className="mt-10 md:mt-16">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">
                  {r.genre}
                </p>
                <h3 className="mt-3 font-display text-4xl md:text-6xl font-black uppercase tracking-tighter text-ink">
                  {r.title}
                </h3>
                <p className="mt-3 font-editorial text-xl text-[color:var(--gold-hi)]/90">
                  releasing on {r.label}
                </p>
              </div>

              {/* Countdown-style meta */}
              <div className="mt-12 flex items-center justify-between border-t border-[color:var(--line)] pt-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-muted">
                  Expected · {r.year}
                </p>
                <div className="flex gap-1.5">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="h-1 w-1 rounded-full bg-[color:var(--gold)]/60"
                      style={{
                        animation: `float-soft 1.4s ease-in-out ${d * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Hover glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(circle at 80% 20%, rgba(201,169,97,0.14), transparent 55%)",
                }}
              />
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
